import type { Permission } from '@softfactura/shared';
import { DREPT_PAGINA_IMPORT } from '@/features/migration/import-permissions';

/** Un drept sau o listă de drepturi, dintre care ajunge oricare. */
type DreptSauOricare = Permission | readonly Permission[];

/**
 * Ce cere o adresă: un drept, o listă (ajunge oricare) sau `{ toate: [...] }` — fiecare element e cerut (un element-listă
 * = oricare dintre ele). „Toate” e pentru paginile de creare care trimit la o listă: fereastra de creare stă acolo, deci
 * trebuie și dreptul listei (ex. /cheltuieli/nou → /cheltuieli?nou=1).
 */
export type RoutePermission = DreptSauOricare | { readonly toate: readonly DreptSauOricare[] };

/**
 * Audit RBAC (2026-06-07): rută → permisiunea de READ cerută de API-ul paginii.
 * Folosit de `RoutePermissionGuard` (montat în dashboard layout) ca să blocheze
 * accesul direct (URL/bookmark) pe pagini pe care rolul userului nu le poate citi
 * (altfel pagina se încarcă și dă 403-uri în consolă).
 *
 * Se ia cel mai LUNG prefix care se potrivește (ex. /stocuri/lichidare > /stocuri).
 *
 * EXCLUSE intenționat:
 *  - /facturi: home/fallback universal (guard-ul redirectează AICI) — nu se guardează (buclă).
 *  - Pagini deja cu <RoleRouteGuard> propriu: /contabilitate, /rapoarte,
 *    /transport-alternativ, /achizitii.
 *  - Incerte (de verificat manual): /trezorerie, /setari (multi-secțiune).
 */
export const ROUTE_PERMISSIONS: ReadonlyArray<{ prefix: string; permission: RoutePermission }> = [
  { prefix: '/proforme', permission: 'invoices.read' },
  { prefix: '/avize', permission: 'invoices.read' },
  { prefix: '/oferte', permission: 'invoices.read' },
  { prefix: '/facturi-recurente', permission: 'invoices.read' },
  { prefix: '/incasari', permission: 'payments.read' },
  { prefix: '/plati-furnizori', permission: 'payments.read' },
  { prefix: '/clienti', permission: 'clients.read' },
  { prefix: '/furnizori', permission: 'clients.read' },
  // lista NIR citește documentele de stoc (GET /stock/documents → stock.read); `receipts.*` = chitanțe
  { prefix: '/receptie', permission: 'stock.read' },
  { prefix: '/cheltuieli', permission: 'expenses.read' },
  // borderourile se văd cu dreptul de citire; încărcarea / confirmarea / ștergerea cer `couriers.use` în pagină
  { prefix: '/borderou-curier', permission: 'couriers.read' },
  { prefix: '/stocuri/lichidare', permission: 'stock.adjust' },
  { prefix: '/stocuri', permission: 'stock.read' },
  { prefix: '/m/scanner', permission: 'stock.adjust' },
  { prefix: '/banca', permission: 'bank_sync.read' },
  { prefix: '/pos', permission: 'pos.use' },
  { prefix: '/trezorerie', permission: 'bank_sync.read' },
  // doar citire ajunge (rolul „Vizualizator”); acțiunile care modifică cer `efactura.receive` în pagină
  { prefix: '/efactura', permission: 'efactura.read' },
  { prefix: '/reges', permission: 'reges.read' },
  // „Import date” (/migrare duce la /setari?tab=migrare): oricare dintre drepturile importurilor de acolo.
  { prefix: '/migrare', permission: DREPT_PAGINA_IMPORT },
  // modificarea de preț în masă (POST /products/bulk-price/*)
  { prefix: '/produse/modificare-pret', permission: 'products.edit' },
  // Paginile de CREARE cer dreptul de creare al serverului (un cont doar de citire e trimis înapoi la listă).
  // Cel mai lung prefix câștigă, deci acestea bat intrările de citire de mai sus.
  { prefix: '/facturi/nou', permission: 'invoices.create' },
  { prefix: '/proforme/nou', permission: 'invoices.create' },
  { prefix: '/avize/nou', permission: 'invoices.create' },
  { prefix: '/oferte/nou', permission: 'invoices.create' },
  { prefix: '/facturi-recurente/nou', permission: 'invoices.create' },
  { prefix: '/facturi-recurente/import', permission: 'invoices.create' },
  // cheltuiala = POST /invoices; fereastra de creare stă în /cheltuieli (`expenses.read`, pe care Operatorul nu-l are)
  { prefix: '/cheltuieli/nou', permission: { toate: ['invoices.create', 'expenses.read'] } },
  // duce la /incasari?nou=1 (`payments.read`): fereastra scrie chitanța (numerar) sau încasarea (restul metodelor)
  { prefix: '/chitante/nou', permission: { toate: [['receipts.create', 'payments.create'], 'payments.read'] } },
  { prefix: '/clienti/nou', permission: 'clients.create' },
  { prefix: '/furnizori/nou', permission: 'clients.create' },
  { prefix: '/produse/nou', permission: 'products.create' },
  { prefix: '/receptie/nou', permission: 'stock.adjust' },
  { prefix: '/stocuri/operatii/receptie/nou', permission: 'stock.adjust' },
  { prefix: '/stocuri/operatii/iesire', permission: 'stock.adjust' },
  { prefix: '/stocuri/operatii/transfer', permission: 'stock.transfer' },
  { prefix: '/stocuri/operatii/inventariere', permission: 'stock.adjust' },
  { prefix: '/stocuri/casari/nou', permission: 'stock.adjust' },
  { prefix: '/stocuri/donatii/nou', permission: 'stock.adjust' },
  { prefix: '/achizitii/nou', permission: 'purchase_orders.write' },
  { prefix: '/contabilitate/imobilizari/nou', permission: 'accounting.chart' },
  { prefix: '/reges/salariati/nou', permission: 'reges.write' },
];

/** Dreptul cerut de o adresă din aplicație (cel mai lung prefix din ROUTE_PERMISSIONS); `null` = fără restricție. */
export function permissionForPath(path: string): RoutePermission | null {
  const pathname = path.split(/[?#]/)[0] || '/';
  const match = ROUTE_PERMISSIONS
    .filter((r) => pathname === r.prefix || pathname.startsWith(r.prefix + '/'))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];
  return match ? match.permission : null;
}

const cereToate = (c: RoutePermission): c is { readonly toate: readonly DreptSauOricare[] } =>
  typeof c === 'object' && 'toate' in c;

/** Utilizatorul poate deschide adresa: nu cere niciun drept sau are ce cere ea (vezi `RoutePermission`). */
export function canOpenPath(path: string, can: (p: Permission) => boolean): boolean {
  const cerut = permissionForPath(path);
  if (!cerut) return true;
  const are = (c: DreptSauOricare) => (typeof c === 'string' ? can(c) : c.some((p) => can(p)));
  return cereToate(cerut) ? cerut.toate.every(are) : are(cerut);
}
